<script setup lang="ts">
import { ArrowUpDown } from 'lucide-vue-next'
import { Card } from '@/ui/card'
import { BasePagination } from '@/ui/pagination'
import { usePinnedColumns } from '@/ui/table'
import type { CellClickEvent, ColumnDef, PaginationState, RowData, SortState } from './interfaces'

const props = withDefaults(
  defineProps<{
    columns: Record<string, ColumnDef>
    rows: RowData[]
    sort?: SortState | null
    pagination?: PaginationState | null
    expandedRows?: Array<number | string>
    isLoading?: boolean
    loaderRowsNumber?: number
    rowClasses?: string
    disabledRowClasses?: string
    disabledTooltipWidth?: string
    cellClasses?: string
    tableTitle?: string | null
    embedded?: boolean
  }>(),
  {
    sort: null,
    pagination: null,
    expandedRows: () => [],
    isLoading: false,
    loaderRowsNumber: 8,
    rowClasses: '',
    disabledRowClasses: 'opacity-50 cursor-not-allowed select-none hover:bg-transparent!',
    disabledTooltipWidth: 'max-w-xs',
    cellClasses: '',
    tableTitle: null,
    embedded: false
  }
)

const emit = defineEmits<{
  rowClicked: [row: RowData]
  cellClicked: [payload: CellClickEvent]
  'update:changePage': [page: number]
  'update:changeSort': [sort: SortState]
}>()

const slots = useSlots()
const { formatNumber } = useNumber()

const hasTitle = computed(() => Boolean(slots.title || props.tableTitle))
const clipClass = computed(() => {
  if (props.embedded) return ''
  return hasTitle.value ? 'rounded-b-xl' : 'rounded-xl'
})

const headerKeys = computed(() => Object.keys(props.columns))
const columnCount = computed(() => headerKeys.value.length)

const { offsetFor } = usePinnedColumns(() => props.columns, headerKeys)

const isSortable = (col: ColumnDef) => props.sort !== null && col.sortable !== false

const isRightAligned = (col: ColumnDef) => col.numeric === true || col.align === 'right'

const displayValue = (col: ColumnDef, value: unknown) => {
  if (col.numeric !== true || typeof value !== 'number' || !Number.isFinite(value)) return value
  return formatNumber(value)
}

const changeSort = (col: ColumnDef) => {
  if (!isSortable(col)) return
  const isSameColumn = props.sort?.sortBy === col.id
  emit('update:changeSort', {
    sortBy: col.id,
    sortOrder: isSameColumn && props.sort?.sortOrder === 'DESC' ? 'ASC' : 'DESC'
  })
}

const isRowExpanded = (rowId: number | string) => props.expandedRows.includes(rowId)

const getRowClasses = (row: RowData) => (row.disabled ? props.disabledRowClasses : props.rowClasses)

const onRowClick = (row: RowData) => {
  if (row.disabled) return
  emit('rowClicked', row)
}

const onChangePage = (page: number) => {
  document.getElementsByTagName('main')[0]?.scrollTo?.({ top: 0 })
  emit('update:changePage', page)
}

const hasPaginator = () => {
  const pagination = props.pagination
  if (!pagination) return false
  if (pagination.lastPage != null) return pagination.lastPage > 1
  if (pagination.hasNextPage || pagination.hasPrevPage) return true
  return !!pagination.total && !!pagination.pageSize && pagination.total > pagination.pageSize
}

const wrapperClass = () => {
  if (!props.embedded) return 'gap-0 py-0'
  return hasPaginator() ? 'border-t border-b' : 'border-t'
}
</script>

<template>
  <div>
    <div v-if="$slots.toolbar" class="mb-4">
      <slot name="toolbar" />
    </div>

    <component :is="embedded ? 'div' : Card" :class="wrapperClass()">
      <CardHeader v-if="!embedded && hasTitle" class="block border-b px-3 py-4! sm:px-6">
        <slot name="title">
          <CardTitle>{{ tableTitle }}</CardTitle>
        </slot>
      </CardHeader>

      <div :class="['overflow-x-auto', clipClass]">
        <Table class="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead
                v-for="key in headerKeys"
                :key="columns[key].id ?? key"
                :pinned="columns[key].pinned"
                :pinned-offset="offsetFor(key)"
                :pinned-width="columns[key].pinnedWidth"
                :class="[
                  'px-3 sm:px-6',
                  isSortable(columns[key]) && 'group/th cursor-pointer',
                  isRightAligned(columns[key]) && 'text-right',
                  columns[key].align === 'center' && 'text-center',
                  columns[key].headerClass
                ]"
                @click="changeSort(columns[key])"
              >
                <slot :name="`th-${key}`" :th="columns[key]">
                  <div
                    class="flex items-center gap-2"
                    :class="[
                      isRightAligned(columns[key]) && 'flex-row-reverse',
                      columns[key].align === 'center' && 'justify-center'
                    ]"
                  >
                    <span>{{ columns[key].label ?? '' }}</span>
                    <ArrowUpDown
                      v-if="isSortable(columns[key])"
                      class="h-4 w-4 shrink-0 transition-opacity"
                      :class="sort?.sortBy === columns[key].id ? 'opacity-100' : 'opacity-0 group-hover/th:opacity-60'"
                    />
                  </div>
                </slot>
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody v-if="isLoading">
            <TableRow v-for="i in loaderRowsNumber" :key="i">
              <template v-if="!$slots.loader">
                <TableCell v-for="col in columnCount" :key="col">
                  <AppSkeleton type="tableCell" class="min-w-full" />
                </TableCell>
              </template>
              <slot name="loader" />
            </TableRow>
          </TableBody>

          <TableBody v-else-if="!rows.length">
            <TableRow class="hover:bg-transparent!">
              <TableCell :colspan="columnCount">
                <div class="m-4 flex min-h-48 flex-col items-center justify-center rounded-lg bg-muted p-4">
                  <slot name="empty-state" />
                </div>
              </TableCell>
            </TableRow>
          </TableBody>

          <TableBody v-else>
            <template v-for="row in rows" :key="row.id">
              <AppTooltip
                :text="row.disabled ? row.disabledTooltip : undefined"
                tooltip-position="top"
                :width="disabledTooltipWidth"
              >
                <TableRow :class="getRowClasses(row)" @click="onRowClick(row)">
                  <TableCell
                    v-for="key in headerKeys"
                    :key="`${key}-${row.id}`"
                    :pinned="columns[key].pinned"
                    :pinned-offset="offsetFor(key)"
                    :pinned-width="columns[key].pinnedWidth"
                    :class="[
                      'px-3 py-4 sm:px-6',
                      !$slots[`td-${key}`] && key === headerKeys[0] && 'font-medium',
                      columns[key].numeric && 'font-semibold tabular-nums text-foreground',
                      isRightAligned(columns[key]) && 'text-right',
                      columns[key].align === 'center' && 'text-center',
                      cellClasses
                    ]"
                    @click="emit('cellClicked', { id: row.id, column: columns[key] })"
                  >
                    <slot :id="row.id" :name="`td-${key}`" :data="row.data" :row="row">
                      {{ displayValue(columns[key], row.data[key]) }}
                    </slot>
                  </TableCell>
                </TableRow>
              </AppTooltip>

              <TableRow v-if="isRowExpanded(row.id) && $slots['expanded-content']" :key="`expanded-${row.id}`">
                <TableCell :colspan="columnCount" class="p-0">
                  <slot name="expanded-content" :row="row" />
                </TableCell>
              </TableRow>
            </template>
          </TableBody>

          <TableFooter v-if="$slots.footer && !pagination && rows.length > 0">
            <slot name="footer" />
          </TableFooter>
        </Table>
      </div>
    </component>

    <BasePagination
      v-if="hasPaginator()"
      :background-color="false"
      :current-page="pagination.currentPage"
      :last-page="pagination.lastPage"
      :total="pagination.total"
      :page-size="pagination.pageSize"
      :has-next-page="pagination.hasNextPage"
      :has-prev-page="pagination.hasPrevPage"
      class="w-full"
      @update:change-page="onChangePage"
    />
  </div>
</template>
